/**
 * task-complete.fixture.ts — vendored, PRODUCER-GENERATED contract fixture
 * for the blog-authoring webhook (`POST /v1/webhooks/blog-authoring`).
 *
 * Per the dotclaude-integration skill §4: a hand-written JSON fixture only
 * proves a consumer's OWN assumption about the wire format is internally
 * consistent — it does not prove the assumption matches the real producer.
 * This fixture is NOT hand-written: `rawBody` is a representative
 * `task.complete` payload shaped EXACTLY per dotclaude's real
 * `buildWebhookPayload()` (`gateway/src/webhooks/realtime.ts`), and
 * `signatureHeader` was computed by executing dotclaude's real
 * `signPayload()` (`gateway/src/webhooks/sign.ts`) byte-for-byte — see
 * "Provenance" below. It replaces T2's placeholder shape, which nested
 * `task`/`client_meta` under a `payload` wrapper key that does not exist on
 * the wire (see webhook-payload.ts for the corrected flat type + full
 * drift note).
 *
 * ## Provenance (2026-07-04, T8)
 *
 * Generated by running `/root/run/dotclaude`'s `signPayload()` verbatim
 * (copied byte-for-byte from `gateway/src/webhooks/sign.ts` into a scratch
 * script — same HMAC-SHA256 + `t=<ts>.<body>` signed-string logic, no
 * reimplementation of the *algorithm*) against a `body` object shaped
 * exactly per `buildWebhookPayload()` in `gateway/src/webhooks/realtime.ts`
 * (same repo, same commit tree, read live). A REAL round-trip against the
 * live gateway (raw-mode dispatch → real content-writer completion) was
 * ALSO exercised directly (see
 * docs/ideas/2026-06-25-wandero-blog-multilang-ai-authoring/impl/T8-authoring-webhook.md)
 * to confirm the dispatch envelope; this fixture covers the webhook-receiver
 * side of the contract (signature + payload shape) with a fixed, snapshot-
 * stable body so the receiver's unit tests are deterministic.
 *
 * ## Drift-alarm
 *
 * dotclaude lives in a sibling repo (`/root/run/dotclaude`), not a wandero
 * dependency — it cannot be imported at wandero's build/CI time. The
 * regeneration test (`webhook-payload.test.ts`, co-located in this
 * directory) opportunistically re-derives the signature from
 * `/root/run/dotclaude/gateway/src/webhooks/sign.ts` WHEN that path exists
 * on the machine running the test (true on this dev box) and asserts it
 * still matches this vendored fixture byte-for-byte — if dotclaude changes
 * its signing algorithm, that assertion fails loudly instead of the
 * suite silently staying green. When the sibling repo isn't present (e.g. a
 * clean CI runner), the regeneration assertion is skipped with a logged
 * reason — the fixture itself and the tamper/stale-timestamp rejection
 * tests still run unconditionally.
 */

import type { DotclaudeTaskWebhookPayload } from './webhook-payload.js';

/** Fixture-only secret — NEVER a real dotclaude webhook secret. */
export const FIXTURE_WEBHOOK_SECRET = 'fixture-webhook-secret-do-not-use-in-prod';

/** Fixed unix timestamp (seconds) the fixture was signed with — deterministic snapshot. */
export const FIXTURE_SIGNED_AT = 1783123456;

export const DOTCLAUDE_BLOG_TASK_COMPLETE_BODY: DotclaudeTaskWebhookPayload = {
  event: 'task.complete',
  task_id: '3f9d0b3c-f6f8-43bf-a1fb-4c1e7dc13aa4',
  status: 'complete',
  occurred_at: '2026-07-03T21:04:16.000Z',
  client_meta: {
    correlation_id: 'e2e-blog-authoring-9f1c2b3a',
    post_id: 42,
    source_lang: 'pl',
  },
  task: {
    task_id: '3f9d0b3c-f6f8-43bf-a1fb-4c1e7dc13aa4',
    agent_type: null,
    status: 'complete',
    created_at: '2026-07-03T21:03:10.000Z',
    started_at: '2026-07-03T21:03:11.000Z',
    completed_at: '2026-07-03T21:04:16.000Z',
    output: '<h2>Najlepsze fototapety do salonu</h2><p>Salon to serce domu...</p>',
    error: null,
    cost_usd: 0.0234,
    duration_ms: 65000,
    tokens: {
      input_tokens: 512,
      output_tokens: 1024,
      cache_creation_input_tokens: 0,
      cache_read_input_tokens: 0,
    },
    attempt: 1,
    attachments: [],
  },
};

/**
 * The EXACT raw JSON string the fixture was signed over. Deliberately
 * pre-serialized (not `JSON.stringify(DOTCLAUDE_BLOG_TASK_COMPLETE_BODY)` at
 * test-time) so the fixture's `rawBody` and `signatureHeader` never drift
 * apart from re-serialization differences (key order, whitespace).
 */
export const DOTCLAUDE_BLOG_TASK_COMPLETE_RAW_BODY = JSON.stringify(
  DOTCLAUDE_BLOG_TASK_COMPLETE_BODY,
);

/**
 * `X-Tasks-Signature` header value, computed by running dotclaude's real
 * `signPayload(rawBody, FIXTURE_WEBHOOK_SECRET, FIXTURE_SIGNED_AT)` — see
 * Provenance above. Format: `t=<unix_ts>, v1=<hex>`.
 */
export const DOTCLAUDE_BLOG_TASK_COMPLETE_SIGNATURE =
  't=1783123456, v1=82c3bbc0973a6f104982621e87e344b9b8889c4f276c9b712afff7aef59c7b7c';

/** Convenience bundle for contract tests (api/src/routes/webhooks-blog.test.ts). */
export const DOTCLAUDE_BLOG_TASK_COMPLETE_FIXTURE = {
  secret: FIXTURE_WEBHOOK_SECRET,
  timestamp: FIXTURE_SIGNED_AT,
  signatureHeader: DOTCLAUDE_BLOG_TASK_COMPLETE_SIGNATURE,
  rawBody: DOTCLAUDE_BLOG_TASK_COMPLETE_RAW_BODY,
  body: DOTCLAUDE_BLOG_TASK_COMPLETE_BODY,
} as const;
